import { useDatasets } from 'api/dataset/dataset.api'
import { useTimeseriesMutation } from 'api/timeseries/timeseries.api'
import { EDIT_TIME_SERIES_DATASET_QUERY_KEY } from 'api/timeseries/timeseries.constants'
import type { DataPointToCreate } from 'api/timeseries/timeseries.types'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { AutoSizer } from 'react-virtualized-auto-sizer'
import { Button, Card, LoadingPlaceholderContainer } from 'ui/atoms'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import EditableDataPointTable from 'ui/molecules/EditableDataPointTable/EditableDataPointTable'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import { EditTimeSeriesProps } from 'ui/uiConfig/anchorComponents/EditTimeSeries/EditTimeSeries'
import Datetime from 'utils/datetime/datetime'
import getLogger from 'utils/log/log'
import {
  alignToHourStart,
  buildGridCoordinates,
  buildGridTimes,
  datasetToTableMatrix,
  GRID_ROW_DURATION,
} from './EditTimeSeriesView.helper'
import styles from './EditTimeSeriesView.module.less'

const log = getLogger('EditTimeSeriesView')

const HEADER_HEIGHT = 200

export default function EditTimeSeriesView(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const [defaultStartTime, defaultEndTime] = useMemo(
    () => [
      Datetime.toISOString(moment().startOf('hour').subtract(1, 'hour').subtract(1, 'week')),
      Datetime.toISOString(moment().startOf('hour').subtract(1, 'minute')),
    ],
    []
  )

  const params = new URLSearchParams(location.search)
  const startTimeParam = params.get('startTime')
  const endTimeParam = params.get('endTime')

  let startTime = undefined
  let endTime = undefined
  try {
    if (startTimeParam != null && endTimeParam != null) {
      // A period can arrive from a shared or hand-edited link, which the date picker's own
      // minute-zeroing never saw.
      startTime = alignToHourStart(Datetime.toISOString(startTimeParam))
      endTime = Datetime.toISOString(endTimeParam)
    } else {
      startTime = defaultStartTime
      endTime = defaultEndTime
    }
  } catch (error) {
    log.error('Error parsing query parameters:', error)
    startTime = defaultStartTime
    endTime = defaultEndTime
  }

  const [pickerPeriod, setPickerPeriod] = useState({
    startTime,
    endTime,
  })
  const [period, setPeriod] = useState<{ startTime: ISODateTime; endTime: ISODateTime }>()
  const [datePickersHaveErrors, setDatePickersHaveErrors] = useState(false)

  const { mutate: createTimeseriesDatapoints, isPending: createDatapointsIsPending } = useTimeseriesMutation()

  const loadPeriod = useCallback(() => {
    setPeriod({
      startTime: alignToHourStart(Datetime.toISOString(pickerPeriod.startTime)),
      endTime: Datetime.toISOString(pickerPeriod.endTime),
    })
  }, [pickerPeriod.endTime, pickerPeriod.startTime])

  // Update URL query params when pickerPeriod changes
  useEffect(() => {
    const startTime = Datetime.toLocalTime(pickerPeriod.startTime, 'standard')
    const endTime = Datetime.toLocalTime(pickerPeriod.endTime, 'standard')

    // Merge startTime/endTime with existing query params so we don't drop others
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.set('startTime', startTime)
    urlParams.set('endTime', endTime)

    const newSearch = urlParams.toString() //keep filter and group params
    navigate({ pathname: window.location.pathname, search: `?${newSearch}` }, { replace: true })
  }, [pickerPeriod, navigate])

  // Initial load
  // biome-ignore lint/correctness/useExhaustiveDependencies: Should only happen once on page load, not every time picker period changes.
  useEffect(() => {
    loadPeriod()
  }, [])

  const { data: editTimeSeriesUiConfig } = useUiConfigAnchorComponent('edit_time_series')
  const { data: editTimeSeriesDatasets = [], status } = useDatasets(
    period === undefined ? -1 : (editTimeSeriesUiConfig?.id ?? -1),
    period === undefined ? -1 : (editTimeSeriesUiConfig?.version ?? -1),
    period === undefined ? [] : editTimeSeriesUiConfig?.dataset_instructions || [],
    period?.startTime,
    period?.endTime,
    {
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: EDIT_TIME_SERIES_DATASET_QUERY_KEY,
    }
  )
  const loadingDatasets = status === 'pending'

  const tableData = useMemo(() => {
    const gridTimes = period === undefined ? [] : buildGridTimes(period.startTime, period.endTime)
    if (gridTimes.length === 0) {
      return []
    }

    const items = (editTimeSeriesUiConfig?.props as EditTimeSeriesProps)?.items || []
    const tableData = datasetToTableMatrix(editTimeSeriesDatasets, gridTimes, items)

    // Translate and prettify time
    tableData.forEach((row, i) => {
      // Header
      if (i === 0) {
        tableData[0][0] = `${t('Time')}\n(${t('Local time')})`
      } else {
        const localTime = Datetime.toLocalTime(row[0] as ISODateTime, 'longDay')
        tableData[i][0] = localTime
      }
    })

    return tableData
  }, [editTimeSeriesDatasets, period, t, editTimeSeriesUiConfig?.props])

  const onSubmit = useCallback(
    (
      changedDataPointValues: {
        columnIndex: number
        rowIndex: number
        value: number | null
      }[]
    ) => {
      if (period === undefined) {
        return // Should never happen
      }

      const { toDataPoint } = buildGridCoordinates(
        editTimeSeriesDatasets,
        buildGridTimes(period.startTime, period.endTime)
      )

      const dataPointsToSave = changedDataPointValues.reduce<DataPointToCreate[]>(
        (dataPoints, { columnIndex, rowIndex, value }) => {
          const dataPoint = toDataPoint({ rowIndex, columnIndex })
          if (dataPoint !== null) {
            dataPoints.push({ ...dataPoint, value })
          }
          return dataPoints
        },
        []
      )

      createTimeseriesDatapoints({ dataPoints: dataPointsToSave, aggregateDuration: GRID_ROW_DURATION })
    },
    [createTimeseriesDatapoints, editTimeSeriesDatasets, period]
  )

  return (
    <>
      <ModuleHeader infoHeader={t('Data Correction')}>
        <div className={styles.EditTimeSeriesView_HeaderContainer}>
          <div className={styles.EditTimeSeriesView_HeaderSpacer} />
          <DatePickerPeriod
            startDate={pickerPeriod.startTime}
            endDate={pickerPeriod.endTime}
            granularity="minute"
            onSelect={(startTime, endTime) => {
              if (startTime && endTime) {
                setPickerPeriod({
                  startTime,
                  endTime,
                })
              }
            }}
            onError={setDatePickersHaveErrors}
          />

          <Button disabled={datePickersHaveErrors} primary marginLeft onClick={loadPeriod}>
            {t('Load period')}
          </Button>
        </div>
      </ModuleHeader>
      <SystemMessageBanner module="edittimeseries" />
      <AutoSizer
        renderProp={({ width, height }) => (
          <div style={{ width: width != null ? `${width}px` : undefined }} className="relative pr-12">
            <Card height={`${Math.max(0, (height ?? 0) - HEADER_HEIGHT)}px`}>
              {loadingDatasets ? (
                <LoadingPlaceholderContainer height={300} />
              ) : (tableData.length ?? 0) > 0 && (tableData.at(0)?.length ?? 0) > 0 ? (
                <EditableDataPointTable
                  value={tableData}
                  onSubmit={onSubmit}
                  isSubmitting={createDatapointsIsPending}
                />
              ) : (
                <div className="italic">{t('No datasets found')}</div>
              )}
            </Card>
          </div>
        )}
      />
    </>
  )
}
